import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import type { GridColDef, GridColumnResizeParams, GridSortItem } from '@mui/x-data-grid';
import { GRID_CHECKBOX_SELECTION_COL_DEF, gridColumnFieldsSelector } from '@mui/x-data-grid';
import { GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD } from '@mui/x-data-grid-premium';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { renameRulesUtils } from 'app-components/flux/rename-rules';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import {
  transactionsActions,
  transactionsSelectors,
  transactionsTypes,
  transactionsUtils,
} from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import { DIALOG_TYPE_MEMORIZED_RULE_EDIT } from 'components/Dialogs/MemorizedRuleEditDialog';
import { Map as ImmutableMap } from 'immutable';
import React from 'react';
import { createAction } from 'redux-actions';
import type { AppDispatch } from 'store';
import { makeStyles } from 'tss-react/mui';
import trackEventTTI from 'utils/trackEventTTI';

export type TransactionGridPrefs = {
  columnVisibilityModel: Record<string, boolean>;
  columnsOrder: string[];
  sortModel: GridSortItem[];
  pinnedColumns?: object;
  columnWidths?: Record<string, number>;
};

export const TextCell = ({ value, ...otherProps }: BoxProps & { value?: string }) => (
  <Tooltip title={value}>
    <Box
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:focus-visible': {
          outline: 'none',
        },
      }}
      tabIndex={0}
      {...otherProps}
    >
      {value}
    </Box>
  </Tooltip>
);

/* eslint-disable tss-unused-classes/unused-classes */
export const useSharedGridStyles = makeStyles()((theme) => ({
  icon: {
    padding: 0,
    height: 20,
    width: 20,
    color: theme.palette.icon.main,
  },
  invoiceIcon: {
    width: 20,
    height: 20,
    color: theme.palette.icon.main,
  },

  reviewedButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    opacity: 0,
    margin: 4,
  },
  hoverReviewed: {
    opacity: 1,
    backgroundColor: theme.palette.icon.light,
    color: theme.palette.background.paper,
    '&:hover, &:focus-within': {
      backgroundColor: theme.palette.amount.positive,
    },
  },
  checked: {
    opacity: 1,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.success.main,
  },
  checkIcon: {
    height: 20,
    width: 20,
    borderRadius: 10,
  },
  unpaidText: {
    color: theme.palette.text.tertiary,
  },
  investmentTransactionTypeField: {
    marginRight: 0,
  },
  investmentTransactionTypeFieldInputRoot: {
    paddingRight: '20px !important',
  },
  amountCell: {
    alignContent: 'center',
  },
  hideRightSeparator: {
    '& > .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  },
  editableTextField: {
    ...theme.typography.paragraphRegular,
  },
  investmentFullWidth: {
    minWidth: '100%',
  },
}));

export const investmentsRegisterStableDefaultPref: TransactionGridPrefs = {
  columnsOrder: [
    GRID_CHECKBOX_SELECTION_COL_DEF.field,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    'date',
    'account',
    'check',
    'action',
    'payee',
    'category',
    'tags',
    'notes',
    'attachments',
    'exclusion',
    'quantity',
    'price',
    'fee',
    'amount',
    'threeDotMenu',
  ],
  columnVisibilityModel: {
    date: false,
    account: true,
    check: false,
    action: true,
    payee: true,
    category: true,
    tags: false,
    notes: false,
    attachments: false,
    exclusion: true,
    quantity: true,
    price: false,
    fee: false,
    amount: true,
    threeDotMenu: true,
  },
  sortModel: [{ field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'desc' }],
  pinnedColumns: {},
};

export const mainRegisterStableDefaultPref: TransactionGridPrefs = {
  columnsOrder: [
    GRID_CHECKBOX_SELECTION_COL_DEF.field,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    'date',
    'account',
    'check',
    'flag',
    'reviewed',
    'status',
    'payee',
    'split',
    'usage',
    'category',
    'tags',
    'notes',
    'attachments',
    'exclusion',
    'clientAndProject',
    'series',
    'unpaid',
    'isInvoiceTxn',
    'amount',
    'threeDotMenu',
  ],
  columnVisibilityModel: {
    date: false,
    account: true,
    check: false,
    flag: false,
    reviewed: false,
    status: true,
    payee: true,
    split: true,
    usage: true,
    category: true,
    tags: true,
    notes: false,
    attachments: false,
    exclusion: true,
    clientAndProject: true,
    series: true,
    unpaid: false,
    isInvoiceTxn: false,
    amount: true,
    threeDotMenu: true,
  },
  sortModel: [{ field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'desc' }],
  pinnedColumns: {},
  columnWidths: {
    date: 200,
    account: 150,
    check: 80,
    flag: 20,
    reviewed: 20,
    status: 86,
    payee: 150,
    split: 20,
    usage: 50,
    category: 150,
    tags: 240,
    notes: 150,
    attachments: 20,
    exclusion: 60,
    clientAndProject: 100,
    series: 20,
    unpaid: 120,
    isInvoiceTxn: 20,
    amount: 120,
    threeDotMenu: 20,
  },
};

// https://mui.com/x/react-data-grid/column-recipes/ use column order as source of truth
export const useColumnsState = (
  apiRef: React.MutableRefObject<GridApiPro>,
  columns: GridColDef[],
  isPrefGrid: boolean,
  stableDefault: TransactionGridPrefs = mainRegisterStableDefaultPref,
  prefV2 = 'sTransactionGrid',
) => {
  const [gridDisplayOptions, setGridDisplayOptions]: [
    TransactionGridPrefs,
    (newVals: Partial<TransactionGridPrefs>) => void,
  ] = usePreferenceV2(prefV2, stableDefault);
  const onColumnOrderChange = React.useCallback(() => {
    if (apiRef.current && isPrefGrid) {
      setGridDisplayOptions({ ...gridDisplayOptions, columnsOrder: gridColumnFieldsSelector(apiRef) });
    }
  }, [apiRef, gridDisplayOptions, isPrefGrid, setGridDisplayOptions]);
  const onColumnWidthChange = React.useCallback(
    (params: GridColumnResizeParams) => {
      const selectedColumn = params.colDef.field;
      const newColumnWidth = params.width;

      if (apiRef.current && isPrefGrid) {
        setGridDisplayOptions({
          ...gridDisplayOptions,
          columnWidths: {
            ...gridDisplayOptions.columnWidths,
            [selectedColumn]: newColumnWidth,
          },
        });
      }
    },
    [apiRef, gridDisplayOptions, isPrefGrid, setGridDisplayOptions],
  );
  const computedColumns = React.useMemo(
    () =>
      !isPrefGrid
        ? columns
        : (gridDisplayOptions.columnsOrder || stableDefault.columnsOrder)?.reduce<GridColDef[]>((acc, field) => {
            const column = columns.find((col) => col.field === field);
            if (!column) {
              return acc;
            }
            const { flex, ...restOfColumnProperties } = column;
            acc.push({
              ...restOfColumnProperties,
              width: gridDisplayOptions.columnWidths?.[column.field] ?? column.width,
            });
            return acc;
          }, []),
    [columns, gridDisplayOptions.columnsOrder, gridDisplayOptions.columnWidths, isPrefGrid, stableDefault.columnsOrder],
  );
  return { columns: computedColumns, onColumnOrderChange, onColumnWidthChange };
};

export const transactionRenameRulesDialog = createAction(
  rootUiActions.createDialog.toString(),
  ({
    transaction,
    handleClose,
    editField,
  }: {
    transaction: TransactionRecord | null;
    handleClose: (rule: TransactionRuleType) => void;
    editField?: string;
  }) => {
    const props = {
      editField,
      fromTxn: true,
      onClose: handleClose,
      onRuleCreated: handleClose,
      memorizedRule: {
        statementPayeeName: renameRulesUtils.normalizePayeeQCS(transaction?.cpData?.payee),
        inferredPayeeName: renameRulesUtils.normalizePayeeQCS(transaction?.cpData?.inferredPayee),
        payee: transaction?.payee,
        coa: transaction?.coa,
      },
    };
    return rootUiTypes.mkRootUiData({
      id: 'add-memorized-rule',
      type: DIALOG_TYPE_MEMORIZED_RULE_EDIT,
      allowNesting: true,
      props: ImmutableMap(props),
    });
  },
);

export const updateInvestmentTransaction = (investmentTransactionObj: TransactionProps, dispatch: AppDispatch) => {
  const investmentTransactionNormalized = transactionsUtils.normalizeInvestmentTransaction(investmentTransactionObj);
  const payload = new transactionsTypes.InvestmentTransaction(investmentTransactionNormalized);
  trackEventTTI(tracker.events.txnSave, transactionsSelectors.getTrackingProps(undefined, payload));
  dispatch(transactionsActions.updateTransaction(payload, { undo: { userMessage: 'Transaction updated.' } }));
};

export const formatInvestmentsUnits = (value: number | null | undefined) => {
  if (!value) {
    return '-';
  }
  const unitsNumber = Math.abs(value);
  if (!Number.isNaN(unitsNumber) && unitsNumber) {
    return unitsNumber % 1 ? unitsNumber.toFixed?.(4) : String(unitsNumber);
    // TODO: add special handling for crypto 0.00000234
  }
  return '-';
};

export const formatInvestmentsPrice = (value: number | null | undefined) => {
  if (!value) {
    return '-';
  }
  const price = Math.abs(value);
  if (!Number.isNaN(price) && price) {
    return price.toFixed?.(2);
  }
  return '-';
};
